'use client';
import { useCallback, useEffect, useState } from 'react';

import { isWebGLSupported } from '@cineamo/ui/src/seat-map-3d/webgl-support';

import { IS_ANALYTICS_ENABLED } from '@cineamo/composables/src/providers/analytics-environment';
import {
    SeatMapViewMode,
    readSeatMapViewMode,
    resolveSeatMapViewMode,
    writeSeatMapViewMode
} from '@cineamo/composables/src/ticket-booking/seat-map-view-mode';

export const SEAT_MAP_DIMENSION_FLAG_KEY = 'seat-map-preferred-dimension';

// 'control' is 2D, the pre-experiment default — PostHog requires an experiment's flag to name its
// first variant 'control', so the keys can't be 2d/3d directly.
const VARIANT_VIEW_MODES: Record<string, SeatMapViewMode> = {
    control: '2D',
    '3d': '3D'
};

/**
 * The view decided for this page session, by the experiment assignment or by the visitor, and the
 * assignment it came from.
 *
 * Module scope rather than state because the seat picker unmounts and remounts as the booking moves
 * between the picking and cart steps: per-mount state would re-run the assignment over a choice the
 * visitor had already made, and would fire a second exposure for the same visit. Only ever written
 * from an effect, so server renders leave both null and nothing leaks between requests.
 */
let sessionViewMode: SeatMapViewMode | null = null;
let sessionAssignedViewMode: SeatMapViewMode | null = null;

const safeLocalStorage = (): Storage | undefined => {
    try {
        return window.localStorage;
    } catch {
        return undefined;
    }
};

/**
 * Starts as 2D on the server and on first client render (the cinema website's HTML is cached),
 * then applies the stored choice after mount — and, for visitors enrolled in the
 * {@link SEAT_MAP_DIMENSION_FLAG_KEY} experiment, the assigned view once flags arrive.
 *
 * The assignment deliberately overrides the stored preference: an experiment that let returning
 * visitors keep their own choice would measure nothing but who had been here before. It applies
 * once per visit, and never on top of a choice made during that visit.
 */
export function useSeatMapViewMode() {
    const [mode, setModeState] = useState<SeatMapViewMode>('2D');
    const [canUse3D, setCanUse3D] = useState(false);
    /** The view the experiment assigned, for switch events to be measured against; null when not enrolled. */
    const [assignedMode, setAssignedMode] = useState<SeatMapViewMode | null>(sessionAssignedViewMode);

    useEffect(() => {
        const supported = isWebGLSupported();
        // Browser-only state, deliberately applied after hydration.
        setCanUse3D(supported);
        setModeState(resolveSeatMapViewMode(sessionViewMode ?? readSeatMapViewMode(safeLocalStorage()), supported));

        // Without WebGL the toggle is not even rendered, so these visitors could only ever see 2D —
        // reading the flag for them would enroll half of them in a 3D variant they cannot be shown.
        if (!IS_ANALYTICS_ENABLED || !supported) return;

        // Reading the flag is what records the `$feature_flag_called` exposure, and this hook is
        // mounted only by the seat picker — so the exposed population is exactly "visitors who
        // reached seat selection", which is what the experiment's metrics are measured against.
        //
        // Imported here rather than at the top of the file so the SDK stays out of the booking
        // bundle of the apps that configure no PostHog key — the cinema websites render this very
        // page and would otherwise pay for a client they never initialize.
        let unsubscribe: (() => void) | undefined;
        let isUnmounted = false;

        void import('posthog-js').then(({ default: posthog }) => {
            if (isUnmounted) return;

            // `onFeatureFlags` fires on registration when flags are already loaded, and again on
            // every reload (identify, property changes); the assignment is taken from the first
            // firing that yields one.
            unsubscribe = posthog.onFeatureFlags(() => {
                if (sessionViewMode) return;

                const variant = posthog.getFeatureFlag(SEAT_MAP_DIMENSION_FLAG_KEY);
                const assigned = typeof variant === 'string' ? VARIANT_VIEW_MODES[variant] : undefined;
                if (!assigned) return;

                sessionViewMode = assigned;
                sessionAssignedViewMode = assigned;
                setAssignedMode(assigned);
                setModeState(assigned);
            });
        });

        return () => {
            isUnmounted = true;
            unsubscribe?.();
        };
    }, []);

    const setMode = useCallback((next: SeatMapViewMode) => {
        sessionViewMode = next;
        setModeState(next);
        writeSeatMapViewMode(safeLocalStorage(), next);
    }, []);

    const disable3D = useCallback(() => {
        // Also pins the session: the 3D renderer failed at runtime, so a remount must not put the
        // assignment back and drop the visitor into the same broken view again.
        sessionViewMode = '2D';
        setCanUse3D(false);
        setModeState('2D');
    }, []);

    return { mode, setMode, canUse3D, disable3D, assignedMode };
}
